import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { HTTP_STATUS_CODE } from '@repo/constants';
import { setTokenOnHeader } from '@repo/services/api';
import { CreateUserRequestDto } from '@repo/services/hooks';
import airbridge from 'airbridge-web-sdk-loader';
import { signInWithCustomToken } from 'firebase/auth';

import { postKakaoLogin, postKakaoToken, postRegisterUser } from 'services/api/auth';
import { auth, firebaseEvent } from 'services/firebase';
import { axiosInstance } from 'services/http';
import { handleFailToast } from 'utils/toast/handleToast';

import { PATH } from 'routers/paths';

async function getKakaoToken(Kakao: any, code: string) {
  const encodedOrigin = encodeURI(window.location.origin);

  try {
    const result = await postKakaoToken(encodedOrigin, code);
    Kakao.Auth.setAccessToken(result.access_token);
    return result.access_token;
  } catch (error) {
    console.error('Kakao Login Error', error);
    throw error;
  }
}

async function registerNewInFirebase(token: string) {
  const { user } = await signInWithCustomToken(auth, token);
  const idToken = await user.getIdToken();
  setTokenOnHeader(axiosInstance, auth, idToken);

  return user;
}

export function useKaKaoLogin() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  const { t } = useTranslation();
  const navigate = useNavigate();
  const redirectURL = localStorage.getItem('redirectURL');

  const { Kakao } = window;

  const handleSignInWithKakao = async () => {
    const source = new URLSearchParams(window.location.search).get('source');

    if (source) {
      sessionStorage.setItem('source', source);
    }

    const origin = window.location.origin + PATH.AUTH.SIGN_IN;

    await Kakao.Auth.authorize({
      redirectUri: origin,
    });
  };

  const loginWithKakao = async (code: string) => {
    if (!code) return;
    try {
      const kakaoToken = await getKakaoToken(Kakao, code);
      const { token } = await postKakaoLogin(kakaoToken);

      await signInWithCustomToken(auth, token);
      localStorage.setItem('isCustomLogin', 'true');

      // Login Event dispatch
      window.dispatchEvent(new Event('isCustomLogin')); // loggedIn
      // window.dispatchEvent(new Event('loggedIn'));

      // airbridge signin sdk
      airbridge.events.send('airbridge.user.signin');

      // 로그인 성공후 로그인 시도한 페이지로 리다이렉팅
      navigate(redirectURL ?? PATH.DASHBOARD);
      localStorage.removeItem('redirectURL');
    } catch (error: any) {
      const isBlockedUser = error.response.data.code === 'BLOCKED';
      const isNewUser = error.response.status === HTTP_STATUS_CODE.FORBIDDEN && !isBlockedUser;
      if (isNewUser) {
        const token = error.response.data.token;
        const source = sessionStorage.getItem('source');

        try {
          const firebaseUser = await registerNewInFirebase(token);

          const reqBody: CreateUserRequestDto = {
            externalId: firebaseUser.uid,
            role: { code: 'USER' },
            displayLanguageCode: 'ko',
            storyLanguageCode: 'ko',
            ...(source && { source }),
          };

          const { id: userId } = await postRegisterUser(reqBody);

          airbridge.events.send('airbridge.user.signup');
          firebaseEvent.signUp({ userId, firebaseUserId: firebaseUser.uid, providerCode: 'KAKAO' });
          handleSignInWithKakao();
        } catch (error: any) {
          if (error.response.data.code === 'UNKNOWN_ERROR_HTTP') {
            handleFailToast(t('general.contact_customer_support_error_message'));
            return;
          }

          handleFailToast(t('general.contact_customer_support_error_message'));
        }
      } else {
        if (isBlockedUser) {
          return;
        }
        handleFailToast(t('login.account_cancel_toast'));
      }
    } finally {
      sessionStorage.removeItem('source');
    }
  };

  useEffect(() => {
    // code가 변경되면, 로그인을 시도
    if (code) {
      loginWithKakao(code);
    }
  }, [code]);

  return {
    handleSignInWithKakao,
  };
}
